import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  ArrowLeft,
  Eye,
  Clock,
  Trash2,
} from "lucide-react";
import { useKnowledgeStore } from "@/stores/knowledge/knowledge-store";
import * as knowledgeApi from "@/services/api/knowledgeApi";
import type { KnowledgeArticle, DomainKnowledgeEntry } from "@/types/knowledge";

interface ArticleViewerProps {
  article: KnowledgeArticle;
}

const statusColor: Record<string, string> = {
  published: "bg-green-100 text-green-800",
  draft: "bg-yellow-100 text-yellow-800",
  stale: "bg-orange-100 text-orange-800",
  archived: "bg-gray-100 text-gray-800",
};

export const ArticleViewer: React.FC<ArticleViewerProps> = ({ article }) => {
  const { viewArticle, backToList, deleteArticle } = useKnowledgeStore();
  const [sources, setSources] = useState<DomainKnowledgeEntry[]>([]);
  const [showSources, setShowSources] = useState(false);
  const [rating, setRating] = useState<"up" | "down" | null>(null);
  const [recompileRequested, setRecompileRequested] = useState(false);

  useEffect(() => {
    if (showSources && sources.length === 0) {
      knowledgeApi.getArticleSources(article.article_id).then(setSources).catch(() => {});
    }
  }, [showSources, article.article_id, sources.length]);

  const handleRate = async (score: number) => {
    try {
      await knowledgeApi.rateArticle(article.article_id, score);
      setRating(score > 0 ? "up" : "down");
    } catch {
      // silent
    }
  };

  const handleRecompile = async () => {
    try {
      await knowledgeApi.recompileArticle(article.article_id);
      setRecompileRequested(true);
    } catch {
      // silent
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Back link */}
      <button
        onClick={backToList}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Knowledge Hub
      </button>

      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-bold">{article.title}</h1>
          <Badge className={statusColor[article.status] || "bg-gray-100"}>
            {article.status}
          </Badge>
        </div>
        <p className="text-lg text-muted-foreground">{article.summary}</p>
        <div className="flex flex-wrap gap-2">
          {article.domain_tags.map((tag) => (
            <Badge key={tag} variant="secondary">
              {tag}
            </Badge>
          ))}
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Eye className="h-4 w-4" />
            {article.view_count} views
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            Compiled {new Date(article.compiled_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Content */}
      <Card className="p-6">
        <div className="prose prose-sm max-w-none dark:prose-invert">
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </div>
      </Card>

      {/* Outgoing links */}
      {article.outgoing_links.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-2">Related Articles</h3>
          <div className="flex flex-wrap gap-2">
            {article.outgoing_links.map((link) => (
              <button
                key={link.article_id}
                onClick={() => viewArticle(link.article_id)}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                <ExternalLink className="h-3 w-3" />
                {link.title}
                <Badge variant="outline" className="text-xs ml-1">
                  {link.relationship}
                </Badge>
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Backlinks */}
      {article.backlinks.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-2">Referenced By</h3>
          <div className="flex flex-wrap gap-2">
            {article.backlinks.map((link) => (
              <button
                key={link.article_id}
                onClick={() => viewArticle(link.article_id)}
                className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
              >
                {link.title}
              </button>
            ))}
          </div>
        </Card>
      )}

      {/* Source entries (collapsible) */}
      <Card className="p-4">
        <button
          onClick={() => setShowSources(!showSources)}
          className="flex items-center gap-2 w-full text-left font-semibold"
        >
          {showSources ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          Source Entries ({article.source_knowledge_ids.length})
        </button>
        {showSources && (
          <div className="mt-3 space-y-2">
            {sources.length === 0 && (
              <p className="text-sm text-muted-foreground">Loading sources...</p>
            )}
            {sources.map((entry) => (
              <div
                key={entry.knowledge_id}
                className="rounded-md border p-3 text-sm"
              >
                <p className="line-clamp-3">{entry.text}</p>
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <Badge variant="outline">{entry.trust_status}</Badge>
                  <span>Score: {(entry.trust_score * 100).toFixed(0)}%</span>
                  <span>{entry.ingestion_path}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">Was this helpful?</span>
        <Button
          variant={rating === "up" ? "default" : "outline"}
          size="sm"
          onClick={() => handleRate(1)}
          disabled={rating !== null}
        >
          <ThumbsUp className="h-4 w-4 mr-1" />
          Yes
        </Button>
        <Button
          variant={rating === "down" ? "destructive" : "outline"}
          size="sm"
          onClick={() => handleRate(-1)}
          disabled={rating !== null}
        >
          <ThumbsDown className="h-4 w-4 mr-1" />
          No
        </Button>

        <div className="ml-auto flex items-center gap-2">
          {article.recompile_needed && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecompile}
              disabled={recompileRequested}
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              {recompileRequested ? "Recompile Requested" : "Request Recompile"}
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Article</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete &quot;{article.title}&quot;? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => deleteArticle(article.article_id)}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Compilation metadata */}
      <div className="text-xs text-muted-foreground border-t pt-3">
        <p>
          Compiled on {new Date(article.compiled_at).toLocaleString()}
        </p>
        {article.last_recompiled_at && (
          <p>Last recompiled: {new Date(article.last_recompiled_at).toLocaleString()}</p>
        )}
      </div>
    </div>
  );
};

export default ArticleViewer;
